/**
 * The brush ring.
 *
 * A circle at the brush's real size, following the pointer. Photoshop, Krita,
 * Procreate and Figma all draw one; we drew nothing, which made brush size
 * knowable only by making a mark and judging it. That loop — mark, undo,
 * adjust, mark again — is most of what makes a paint tool feel like it is
 * fighting you.
 *
 * Three decisions worth the words:
 *
 *   **The system cursor stays.** Krita's default is outline *plus* crosshair,
 *   and it is right for two reasons: the crosshair marks the exact centre, and
 *   a 2px brush on a half-scale canvas draws a 1px ring that is effectively
 *   invisible. Rather than clamp the ring to a minimum — which would make it
 *   lie about size, the one thing it exists to tell the truth about — the
 *   crosshair carries the small end.
 *
 *   **The eraser's ring is dashed, not just a different colour.** It is the
 *   destructive mode, and a form difference survives colour blindness, a
 *   sunlit screen and a glance.
 *
 *   **Never shown for touch.** There is no hover, so a ring would sit wherever
 *   the finger last lifted — a confident statement about where the brush is
 *   that is wrong the moment it is made.
 */
import { CANVAS } from "../../shared/format";
import type { Tools } from "./tools";

/**
 * How wide the ring is on screen, in CSS pixels.
 *
 * Pure and exported because this is the part that is silently wrong. The canvas
 * is letterboxed by CSS, so a 24-template-pixel brush is 24 screen pixels at
 * exactly one pane width and at no other. Reading `brush.size` and using it
 * directly gives a ring that is confidently, invisibly wrong at every zoom —
 * the same ratio `toTemplate()` uses is the only correct one.
 *
 * Returns 0 when the canvas has no laid-out size, which happens on the frame
 * before the first layout and would otherwise produce NaN.
 */
export function ringDiameter(sizeInTemplatePx: number, renderedWidth: number): number {
  if (!(renderedWidth > 0)) return 0;
  return sizeInTemplatePx * (renderedWidth / CANVAS.width);
}

export interface BrushRingOptions {
  canvas: HTMLCanvasElement;
  tools: Tools;
}

/** Tools that paint with a round tip. Anything else has no ring to draw. */
const ROUND_TIP = new Set(["brush", "eraser"]);

export function mountBrushRing({ canvas, tools }: BrushRingOptions) {
  const ring = document.createElement("div");
  ring.className = "brush-ring";
  ring.setAttribute("aria-hidden", "true");
  document.body.appendChild(ring);

  let size = tools.get().brush.size;
  let erasing = tools.get().active === "eraser";
  let shown = false;

  const unsubscribe = tools.subscribe((state) => {
    size = state.brush.size;
    erasing = state.active === "eraser";
    ring.dataset.tool = state.active;
    // A tool without a round tip hides the ring rather than lying about shape.
    if (!ROUND_TIP.has(state.active)) hide();
  });

  function hide() {
    if (!shown) return;
    shown = false;
    ring.removeAttribute("data-show");
  }

  function moveTo(e: PointerEvent) {
    // No hover on touch, so no honest place to put a ring.
    if (e.pointerType === "touch" || !ROUND_TIP.has(tools.get().active)) {
      hide();
      return;
    }

    /*
      Position is fixed to the viewport rather than to the stage. The stage
      would need its own positioning context and scroll maths, and the ring
      genuinely does extend past the canvas edge when the brush is large — that
      is what the brush does, so it is what the ring should show.
    */
    const rect = canvas.getBoundingClientRect();
    const d = ringDiameter(size, rect.width);
    if (d <= 0) {
      hide();
      return;
    }

    ring.style.inlineSize = `${d}px`;
    ring.style.blockSize = `${d}px`;
    ring.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0) translate(-50%, -50%)`;
    ring.dataset.mode = erasing ? "erase" : "paint";

    if (!shown) {
      shown = true;
      ring.setAttribute("data-show", "");
    }
  }

  /*
    Bound to the stage, not the canvas: the canvas is letterboxed inside it, and
    a ring that vanished in the padding would flicker at every edge approach.
    Written directly rather than through requestAnimationFrame — a cursor that
    trails the pointer by a frame reads as lag, which is the opposite of the
    precision this exists to provide.
  */
  const stage = canvas.closest<HTMLElement>(".canvas-stage") ?? canvas;
  stage.addEventListener("pointermove", moveTo);
  stage.addEventListener("pointerdown", moveTo);
  stage.addEventListener("pointerleave", hide);
  // A pointer that leaves by being cancelled never fires pointerleave.
  stage.addEventListener("pointercancel", hide);
  window.addEventListener("blur", hide);

  return {
    destroy() {
      unsubscribe();
      stage.removeEventListener("pointermove", moveTo);
      stage.removeEventListener("pointerdown", moveTo);
      stage.removeEventListener("pointerleave", hide);
      stage.removeEventListener("pointercancel", hide);
      window.removeEventListener("blur", hide);
      ring.remove();
    },
  };
}
